import { useCallback, useEffect, useMemo, useState, type JSX, type PropsWithChildren } from 'react';
import i18next from 'i18next';
import { setActiveCulture } from './activeCulture';
import { cultureFromNavigator, cultureToLanguage, isCulture, type Culture } from './cultures';
import { cultureStorageKey, LanguageContext } from './languageContext';

const readStoredCulture = (): Culture | null => {
  try {
    const stored: unknown = localStorage.getItem(cultureStorageKey());
    return isCulture(stored) ? stored : null;
  } catch {
    return null;
  }
};

const storeCulture = (culture: Culture): void => {
  try {
    localStorage.setItem(cultureStorageKey(), culture);
  } catch {
    // Storage unavailable: keep the choice in memory only.
  }
};

/** Optional hook for the native shell: called after every culture change (`setLocale` bridge call). */
export type CultureChangeListener = (culture: Culture) => void;

interface LanguageProviderProps {
  initialCulture?: Culture;
  onCultureChange?: CultureChangeListener;
}

export const LanguageProvider = ({
  children,
  initialCulture,
  onCultureChange
}: PropsWithChildren<LanguageProviderProps>): JSX.Element => {
  const [culture, setCultureState] = useState<Culture>(() => {
    const initial =
      initialCulture ?? readStoredCulture() ?? cultureFromNavigator(navigator.language);
    // Published here rather than from an effect: `http.ts` reads it outside React
    // to send Accept-Language, and a descendant's request can fire before this
    // provider's effect, since child effects run first. With `initialCulture`
    // overriding what is stored, the reader's storage fallback would be stale —
    // a layout effect would not help either, being just as late relative to a
    // child. The write is idempotent, so StrictMode's double invoke is harmless.
    setActiveCulture(initial);
    return initial;
  });

  useEffect(() => {
    void i18next.changeLanguage(cultureToLanguage(culture));
    // Parity with _OperationsLayout: <html lang> stays "en" regardless of culture (inventory §1.1).
  }, [culture]);

  const setCulture = useCallback(
    (next: Culture) => {
      setCultureState(next);
      setActiveCulture(next);
      storeCulture(next);
      onCultureChange?.(next);
    },
    [onCultureChange]
  );

  const value = useMemo(() => ({ culture, setCulture }), [culture, setCulture]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
